import { type MouseEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPanel, selectWeatherTimeline } from "features/map/control/selectors"
import { setWeatherCurrentTime, setWeatherIsPlaying } from "features/map/control/slice"
import { useWeatherTimelineWindow } from "../series/weatherTimelineWindow"
import { useWeatherFrameTimes } from "../series/weatherFrameIndex"
import { displayedFrameTime, renderableSpans, spanCovers } from "../series/frameResolution"
import { useSettledView } from "../shared/weatherViewport"
import { useTimelineDensity, bucketizeDensity } from "../series/timelineDensity"
import { TimelineDensityGraph, GRAPH_HEIGHT_PX } from "./TimelineDensityGraph"
import { LIGHTNING_STRIKES_MIN_ZOOM } from "../lightning/lightningStrikesLayers"
import { WeatherShimmer } from "./WeatherShimmer"

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
const QUARTER_HOUR_MS = 15 * 60 * 1000
const TICK_PX = 24
const NOW_BADGE_RADIUS_PX = 10
const nowHoleMask = (x: number | null, y: number) =>
    x === null
        ? undefined
        : `radial-gradient(circle ${NOW_BADGE_RADIUS_PX}px at ${x}px ${y}px, transparent ${NOW_BADGE_RADIUS_PX - 0.5}px, #000 ${NOW_BADGE_RADIUS_PX + 0.5}px)`
const MIN_DISPATCH_INTERVAL_MS = 33
const DENSITY_BRACKET_MS = 5 * 60 * 1000
const TRACK_TOP_PX = 15
const GRAPH_OVERFLOW_PX = GRAPH_HEIGHT_PX - TRACK_TOP_PX
const LIGHTNING_SPEED_FACTOR = 1 / 3
const PLAYBACK_STALL_TIMEOUT_MS = 5000
const MIN_BAR_PX = 6

const timeLabelFormatter = new Intl.DateTimeFormat("en-CA", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
})

const relativeDayLabels: Record<number, string> = {
    [-1]: "Yesterday",
    0: "Today",
    1: "Tomorrow",
}

const startOfLocalDay = (ms: number) => {
    const date = new Date(ms)
    date.setHours(0, 0, 0, 0)
    return date.getTime()
}

// Rounded, not floored: a DST shift makes the span between local midnights 23h or 25h.
const localDayOffset = (ms: number, nowMs: number) =>
    Math.round((startOfLocalDay(ms) - startOfLocalDay(nowMs)) / DAY_MS)

const formatTimeLabel = (iso: string, nowMs: number) => {
    const parts = timeLabelFormatter.formatToParts(new Date(iso))
    const get = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((p) => p.type === type)?.value ?? ""
    const time = `${get("hour")}:${get("minute")} (${get("timeZoneName")})`
    const relativeDay = relativeDayLabels[localDayOffset(Date.parse(iso), nowMs)]
    return relativeDay
        ? `${relativeDay}, ${time}`
        : `${get("weekday")} ${get("month")} ${get("day")}, ${time}`
}

const hourTickFormatter = new Intl.DateTimeFormat("en-CA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
})

type Props = { isLight: boolean }

export const WeatherTimelineControls = ({ isLight }: Props) => {
    const timeline = useAppSelector(selectWeatherTimeline)
    const panel = useAppSelector(selectWeatherPanel)
    const density = useTimelineDensity(panel.mode)
    const frameTimesMs = useWeatherFrameTimes(panel.mode)
    const { nowMs, startMs, endMs } = useWeatherTimelineWindow(panel.mode)
    const dispatch = useAppDispatch()
    const settledView = useSettledView(panel.mode === "lightning")
    const knockoutId = `wx-play-knockout-${useId().replace(/:/g, "")}`

    const scrollRef = useRef<HTMLDivElement>(null)
    const draggingRef = useRef(false)
    const dragStartXRef = useRef(0)
    const dragStartScrollRef = useRef(0)

    // Suspends playback for the duration of a scrub without touching `isPlaying`, so the
    // play/pause button keeps its state and playback picks up from wherever the drag ended.
    const [isScrubbing, setIsScrubbing] = useState(false)

    const [isNowVisible, setIsNowVisible] = useState(true)
    const [nowMarkerEl, setNowMarkerEl] = useState<HTMLDivElement | null>(null)
    const nowMarkerRef = useCallback((node: HTMLDivElement | null) => setNowMarkerEl(node), [])

    useEffect(() => {
        if (!nowMarkerEl) {
            setIsNowVisible(false)
            return
        }
        const observer = new IntersectionObserver(
            ([entry]) => setIsNowVisible(entry.isIntersecting),
            { root: scrollRef.current, threshold: 0 }
        )
        observer.observe(nowMarkerEl)
        return () => observer.disconnect()
    }, [nowMarkerEl])

    const currentMs = Date.parse(timeline.currentTime)
    const totalMs = endMs - startMs || 1

    const spans = useMemo(() => renderableSpans(frameTimesMs), [frameTimesMs])

    const totalTicks = Math.floor(totalMs / QUARTER_HOUR_MS) + 1
    const trackWidth = (totalTicks - 1) * TICK_PX
    const playheadX = Math.round(((currentMs - startMs) / totalMs) * trackWidth)
    const nowX =
        nowMs < startMs || nowMs > endMs
            ? null
            : Math.round(((nowMs - startMs) / totalMs) * trackWidth)

    const startMsRef = useRef(startMs)
    const endMsRef = useRef(endMs)
    const totalMsRef = useRef(totalMs)
    const trackWidthRef = useRef(trackWidth)
    startMsRef.current = startMs
    endMsRef.current = endMs
    totalMsRef.current = totalMs
    trackWidthRef.current = trackWidth

    const lastTickRef = useRef<number | null>(null)
    const lastDispatchRef = useRef<number>(0)
    const playheadMsRef = useRef(currentMs)
    const stalledSinceRef = useRef<number | null>(null)

    useEffect(() => {
        if (!timeline.isPlaying || isScrubbing) {
            lastTickRef.current = null
            return
        }
        const resumeMs = Date.parse(timeline.currentTime)
        playheadMsRef.current = resumeMs >= endMsRef.current ? startMsRef.current : resumeMs
        stalledSinceRef.current = null
        let rafId = 0
        const tick = (now: number) => {
            if (lastTickRef.current == null) lastTickRef.current = now
            const realDeltaSeconds = (now - lastTickRef.current) / 1000
            lastTickRef.current = now

            const speed =
                timeline.playbackSpeed * (panel.mode === "lightning" ? LIGHTNING_SPEED_FACTOR : 1)
            const dataDeltaMs = realDeltaSeconds * speed * HOUR_MS
            const nextMs = playheadMsRef.current + dataDeltaMs
            if (nextMs >= endMsRef.current) {
                dispatch(setWeatherCurrentTime(new Date(endMsRef.current).toISOString()))
                dispatch(setWeatherIsPlaying(false))
                return
            }

            // A hole that is merely still in flight is buffered through rather than paused on,
            // since the pump will land it; only one that outlasts the timeout ends playback.
            if (spansRef.current.length > 0 && !spanCovers(spansRef.current, nextMs)) {
                stalledSinceRef.current ??= now
                if (now - stalledSinceRef.current >= PLAYBACK_STALL_TIMEOUT_MS) {
                    dispatch(setWeatherIsPlaying(false))
                    return
                }
                rafId = requestAnimationFrame(tick)
                return
            }
            stalledSinceRef.current = null
            playheadMsRef.current = nextMs

            if (now - lastDispatchRef.current >= MIN_DISPATCH_INTERVAL_MS) {
                dispatch(setWeatherCurrentTime(new Date(nextMs).toISOString()))
                lastDispatchRef.current = now
            }

            rafId = requestAnimationFrame(tick)
        }
        rafId = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(rafId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeline.isPlaying, timeline.playbackSpeed, panel.mode, isScrubbing])

    useEffect(() => {
        if (draggingRef.current) return
        const container = scrollRef.current
        if (!container) return
        container.scrollLeft = playheadX
    }, [playheadX])

    useEffect(() => {
        const onMouseMove = (e: globalThis.MouseEvent) => {
            if (!draggingRef.current || !scrollRef.current) return

            const deltaX = dragStartXRef.current - e.clientX
            const newScrollLeft = Math.max(
                0,
                Math.min(trackWidthRef.current, dragStartScrollRef.current + deltaX)
            )
            scrollRef.current.scrollLeft = newScrollLeft
            const newMs =
                startMsRef.current + (newScrollLeft / trackWidthRef.current) * totalMsRef.current
            dispatch(
                setWeatherCurrentTime(
                    new Date(
                        Math.max(startMsRef.current, Math.min(endMsRef.current, newMs))
                    ).toISOString()
                )
            )
        }
        const onMouseUp = () => {
            if (!draggingRef.current) return
            draggingRef.current = false
            setIsScrubbing(false)
        }
        document.addEventListener("mousemove", onMouseMove)
        document.addEventListener("mouseup", onMouseUp)
        return () => {
            document.removeEventListener("mousemove", onMouseMove)
            document.removeEventListener("mouseup", onMouseUp)
        }
    }, [dispatch])

    const onDragStart = (e: MouseEvent<HTMLDivElement>) => {
        if ((e.target as Element).closest("button")) return
        e.preventDefault()

        setIsScrubbing(true)
        draggingRef.current = true
        dragStartXRef.current = e.clientX
        dragStartScrollRef.current = scrollRef.current?.scrollLeft ?? 0
    }

    const togglePlay = () => dispatch(setWeatherIsPlaying(!timeline.isPlaying))

    const strikeDetailVisible =
        panel.mode === "lightning" &&
        settledView !== null &&
        settledView.zoom >= LIGHTNING_STRIKES_MIN_ZOOM &&
        currentMs <= Date.now()
    const displayIso = strikeDetailVisible
        ? timeline.currentTime
        : (displayedFrameTime(frameTimesMs, currentMs) ?? timeline.currentTime)
    const timeLabel = useMemo(() => formatTimeLabel(displayIso, nowMs), [displayIso, nowMs])

    const spansRef = useRef(spans)
    spansRef.current = spans

    const densityGraph = useMemo(() => {
        if (!density || trackWidth <= 0) return null
        const bucketCount = Math.ceil(totalMs / DENSITY_BRACKET_MS)
        const counts = bucketizeDensity(density, startMs, DENSITY_BRACKET_MS, bucketCount)
        const pxPerMs = trackWidth / totalMs
        return {
            counts,
            x0: 0.5 * DENSITY_BRACKET_MS * pxPerMs,
            dx: DENSITY_BRACKET_MS * pxPerMs,
        }
    }, [density, trackWidth, totalMs, startMs])

    const hasForwardData = spanCovers(spans, currentMs) && spans[spans.length - 1][1] > currentMs
    const canTogglePlay = timeline.isPlaying || hasForwardData

    const goToNow = () => {
        if (timeline.isPlaying) dispatch(setWeatherIsPlaying(false))
        const target = Math.max(startMs, Math.min(endMs, Date.now()))
        dispatch(setWeatherCurrentTime(new Date(target).toISOString()))
    }

    const loadedBars = useMemo(() => {
        const pxPerMs = trackWidth / totalMs
        return spans.map(([spanStartMs, spanEndMs]) => {
            const left = (spanStartMs - startMs) * pxPerMs
            return {
                spanStartMs,
                left,
                width: Math.max(MIN_BAR_PX, (spanEndMs - startMs) * pxPerMs - left),
            }
        })
    }, [spans, startMs, totalMs, trackWidth])

    const rulerTicks = useMemo(
        () =>
            Array.from({ length: totalTicks }, (_, i) => {
                const tickMs = startMs + i * QUARTER_HOUR_MS
                const isHour = tickMs % HOUR_MS === 0
                const underNowBadge =
                    nowX !== null && Math.abs(i * TICK_PX - nowX) < NOW_BADGE_RADIUS_PX
                return (
                    <div key={i} className="absolute top-0" style={{ left: i * TICK_PX }}>
                        {!underNowBadge && (
                            <div
                                className={classNames("w-0.5", {
                                    "bg-gray-200": isLight,
                                    "bg-gray-600": !isLight,
                                    "h-[14px]": isHour,
                                    "h-1.5": !isHour,
                                })}
                            />
                        )}
                        {isHour && (
                            <span
                                className={classNames(
                                    "absolute top-[14px] font-mono text-[12px] font-normal leading-[140%] whitespace-nowrap -translate-x-1/2",
                                    {
                                        "text-black-500": isLight,
                                        "text-gray-400": !isLight,
                                    }
                                )}
                            >
                                {hourTickFormatter.format(new Date(tickMs))}
                            </span>
                        )}
                    </div>
                )
            }),
        [totalTicks, startMs, isLight, nowX]
    )

    return (
        <div
            className="flex items-center gap-3 w-full h-full px-5 py-2 cursor-grab active:cursor-grabbing"
            onMouseDown={onDragStart}
        >
            <button
                type="button"
                onClick={togglePlay}
                disabled={!canTogglePlay}
                aria-label={timeline.isPlaying ? "Pause" : "Play"}
                className={classNames(
                    "flex-shrink-0 h-9 w-9 transition-colors focus:outline-none",
                    isLight
                        ? classNames("text-gray-400", canTogglePlay && "hover:text-black-500")
                        : classNames("text-white-300", canTogglePlay && "hover:text-white-600"),
                    !canTogglePlay && "opacity-40 cursor-not-allowed"
                )}
            >
                <svg viewBox="0 0 36 36" className="h-full w-full" aria-hidden="true">
                    <mask id={knockoutId}>
                        <rect width="36" height="36" fill="white" />
                        <path
                            transform="translate(6 6)"
                            fill="black"
                            d={
                                timeline.isPlaying
                                    ? "M14 18.5V5.5H17.75V18.5H14ZM6.25 18.5V5.5H10V18.5H6.25Z"
                                    : "M8.21094 18.0963V5.9043L17.7877 12.0003L8.21094 18.0963Z"
                            }
                        />
                    </mask>
                    <circle
                        cx="18"
                        cy="18"
                        r="18"
                        fill="currentColor"
                        mask={`url(#${knockoutId})`}
                    />
                </svg>
            </button>

            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <div className="relative z-10 flex items-center justify-center gap-1">
                    <button
                        type="button"
                        aria-label="Skip back 12 hours"
                        onClick={() => {
                            if (timeline.isPlaying) dispatch(setWeatherIsPlaying(false))
                            dispatch(
                                setWeatherCurrentTime(
                                    new Date(
                                        Math.max(startMs, currentMs - 12 * HOUR_MS)
                                    ).toISOString()
                                )
                            )
                        }}
                        className={classNames("p-0.5", {
                            ["text-gray-900 hover:text-gray-600"]: isLight,
                            ["text-gray-400 hover:text-gray-200"]: !isLight,
                        })}
                    >
                        <Icons.SmallIcon Icon={Icons.FirstPage} />
                    </button>
                    <div
                        className={classNames(
                            "w-[23ch] flex-shrink-0 whitespace-nowrap font-mono text-[12px] font-normal leading-[140%] text-center",
                            {
                                ["text-gray-900"]: isLight,
                                ["text-gray-0"]: !isLight,
                            }
                        )}
                    >
                        {timeLabel}
                    </div>
                    <div className="relative flex-shrink-0">
                        <button
                            type="button"
                            aria-label="Skip forward 12 hours"
                            onClick={() => {
                                if (timeline.isPlaying) dispatch(setWeatherIsPlaying(false))
                                dispatch(
                                    setWeatherCurrentTime(
                                        new Date(
                                            Math.min(endMs, currentMs + 12 * HOUR_MS)
                                        ).toISOString()
                                    )
                                )
                            }}
                            className={classNames("p-0.5", {
                                ["text-gray-900 hover:text-gray-600"]: isLight,
                                ["text-gray-400 hover:text-gray-200"]: !isLight,
                            })}
                        >
                            <Icons.SmallIcon Icon={Icons.LastPage} />
                        </button>

                        {!isNowVisible && (
                            <button
                                type="button"
                                aria-label="Go to now"
                                onClick={goToNow}
                                className={classNames(
                                    "absolute left-full top-1/2 ml-2 -translate-y-1/2 flex items-center justify-center gap-1 rounded-lg border px-2 py-0.5 text-[13px] font-medium",
                                    {
                                        ["border-[rgba(12,12,15,0.24)] bg-white text-gray-900 hover:bg-gray-50"]:
                                            isLight,
                                        ["border-white-200 bg-gray-700 text-gray-100 hover:bg-gray-500"]:
                                            !isLight,
                                    }
                                )}
                            >
                                <Icons.SmallIcon Icon={Icons.Refresh} />
                                Now
                            </button>
                        )}
                    </div>
                </div>

                <div className="relative z-0" style={{ height: 60 }}>
                    <div
                        className="absolute left-0 right-0 bottom-0 overflow-hidden"
                        style={{ top: -GRAPH_OVERFLOW_PX }}
                    >
                        <div
                            className={classNames(
                                "absolute left-1/2 z-10 pointer-events-none h-[35px] w-px -translate-y-1/2",
                                {
                                    ["bg-gray-900"]: isLight,
                                    ["bg-white"]: !isLight,
                                }
                            )}
                            style={{
                                top: 18 + GRAPH_OVERFLOW_PX,
                                maskImage: nowHoleMask(
                                    nowX === null ? null : nowX - playheadX + 0.5,
                                    17.5
                                ),
                            }}
                        />

                        <div
                            ref={scrollRef}
                            className="absolute left-0 right-0 overflow-x-auto select-none"
                            style={{ top: 0, bottom: -20, scrollbarWidth: "none" }}
                        >
                            <div
                                style={{
                                    display: "inline-block",
                                    verticalAlign: "top",
                                    paddingLeft: "50%",
                                    paddingRight: "50%",
                                    marginTop: GRAPH_HEIGHT_PX,
                                }}
                            >
                                <div className="relative" style={{ width: trackWidth }}>
                                    {densityGraph && (
                                        <TimelineDensityGraph
                                            counts={densityGraph.counts}
                                            x0={densityGraph.x0}
                                            dx={densityGraph.dx}
                                            width={trackWidth}
                                            isLight={isLight}
                                            scrollRef={scrollRef}
                                        />
                                    )}
                                    <div
                                        className="relative h-[6px] overflow-hidden rounded-full"
                                        style={{ maskImage: nowHoleMask(nowX, 3) }}
                                    >
                                        <div
                                            className={classNames("absolute inset-0", {
                                                "bg-gray-200": isLight,
                                                "bg-gray-600": !isLight,
                                            })}
                                        />
                                        {loadedBars.map(({ spanStartMs, left, width }) => (
                                            <div
                                                key={spanStartMs}
                                                className="absolute top-0 h-full rounded-full bg-gray-400"
                                                style={{ left, width }}
                                            />
                                        ))}
                                    </div>

                                    {(() => {
                                        if (nowX === null) return null
                                        return (
                                            <div
                                                ref={nowMarkerRef}
                                                className={classNames(
                                                    "pointer-events-none absolute top-1/2 z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2",
                                                    isLight ? "text-black-1000" : "text-white-1000"
                                                )}
                                                style={{ left: nowX }}
                                            >
                                                <svg
                                                    viewBox="0 0 20 20"
                                                    className="h-full w-full"
                                                    aria-hidden="true"
                                                >
                                                    <Icons.Clock
                                                        x={2}
                                                        y={2}
                                                        width={16}
                                                        height={16}
                                                    />
                                                </svg>
                                            </div>
                                        )
                                    })()}

                                    <div className="relative h-auto">{rulerTicks}</div>
                                </div>
                            </div>
                        </div>

                        <div
                            className="pointer-events-none absolute left-0 h-[6px] w-full overflow-hidden bg-transparent z-[100]"
                            style={{ top: TRACK_TOP_PX + GRAPH_OVERFLOW_PX }}
                        >
                            <WeatherShimmer isLight={isLight} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
